import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

@Component({
  selector: 'app-reference-number',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, MatSnackBarModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="ref-wrapper" *ngIf="referenceNumber">
      <span class="ref-text">{{ display || referenceNumber }}</span>
      <mat-icon class="ref-action"
                matTooltip="Copy reference number"
                (click)="copy($event)">content_copy</mat-icon>
      <mat-icon class="ref-action"
                matTooltip="Open reference page in new tab"
                (click)="open($event)">open_in_new</mat-icon>
    </span>
  `,
  styles: [`
    :host { display: inline-flex; align-items: center; }
    .ref-wrapper { display: inline-flex; align-items: center; gap: 4px; }
    .ref-text { display: inline-flex; align-items: center; gap: 4px; }
    .ref-action {
      font-size: 14px;
      width: 14px;
      height: 14px;
      line-height: 14px;
      cursor: pointer;
      color: #6b7280;
      opacity: 0;
      transition: opacity 0.15s, color 0.15s;
    }
    .ref-action:hover { color: #3b82f6; }
    :host:hover .ref-action,
    .ref-wrapper:hover .ref-action { opacity: 1; }
  `],
})
export class ReferenceNumberComponent {
  @Input() referenceNumber: string | null | undefined;
  /** Optional display text override — useful when the UI shows a truncated form
   * (e.g. last 8 chars) but copy/open should still use the full referenceNumber. */
  @Input() display: string | null | undefined;

  private readonly snackBar = inject(MatSnackBar);

  copy(event: Event): void {
    event.stopPropagation();
    if (!this.referenceNumber) return;
    navigator.clipboard.writeText(this.referenceNumber).then(
      () => this.snackBar.open('Reference number copied', 'Close', { duration: 2000 }),
      () => this.snackBar.open('Failed to copy', 'Close', { duration: 3000 }),
    );
  }

  open(event: Event): void {
    event.stopPropagation();
    if (!this.referenceNumber) return;
    window.open(`/reference-numbers?search=${encodeURIComponent(this.referenceNumber)}`, '_blank');
  }
}
